import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

const baseUrl =
  process.env.NODE_ENV === 'production'
    ? 'https://apiv1.designer.neku.cc'
    : // 'https://neku-server-1-m3dlsop5cq-de.a.run.app'
      process.env.REACT_APP_NEKU_API

export const useAuthStore = create(
  devtools((set) => ({
    auth: JSON.parse(localStorage.getItem('auth') || '{}'),
    setToken: (auth) => {
      localStorage.setItem('auth', JSON.stringify(auth))
      set({ auth })
    },
    setFace: (face) => {
      const auth = JSON.parse(localStorage.getItem('auth') || '{}')
      auth.face = face
      localStorage.setItem('auth', JSON.stringify(auth))
      set({ auth })
    },
    removeToken: () => {
      localStorage.removeItem('auth')
      set({ auth: '{}' })
    },
  })),
)

const fetchMyPermissions = async (token) => {
  const url = new URL(`${baseUrl}/permissions/me`)
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    return {}
  }
  return response.json()
}

/**
 * 处理认证响应，提取 token 和用户信息并保存到 store
 * @param {Object} res - 后端返回的响应数据，包含 accessToken 和用户信息
 * @returns {Promise<Object>} - 返回原始响应数据
 */
export const processAuthResponse = async (res) => {
  const { accessToken, ...user } = res
  const token = accessToken
  const face = { id: user.id, nick_name: user.nick_name }
  useAuthStore.getState().setToken({ token, user, face, featurePermissions: {} })
  try {
    const featurePermissions = await fetchMyPermissions(token)
    const auth = useAuthStore.getState().auth
    useAuthStore.getState().setToken({ ...auth, featurePermissions })
  } catch {
    // 权限拉取失败不阻塞登录
  }
  return res
}

const TRANSIENT_HTTP_STATUS = new Set([502, 503, 504])
const DEPLOY_RETRY_DELAY_MS = 500

const isTransientFetchError = (error) => {
  if (!error || error.name === 'AbortError') return false
  const status = error.response?.status
  if (typeof status === 'number') return TRANSIENT_HTTP_STATUS.has(status)
  return true
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const requestOnce = async (endpoint, params = {}, options = {}) => {
  const token = useAuthStore.getState().auth?.token

  const url = new URL(`${baseUrl}${endpoint}`)
  if (params) {
    const validParams = assembleParams(params)
    Object.keys(validParams).forEach((key) => {
      const value = validParams[key]
      // 如果是数组，需要多次 append 同一个 key
      if (Array.isArray(value)) {
        value.forEach((item) => {
          url.searchParams.append(key, item)
        })
      } else {
        url.searchParams.append(key, value)
      }
    })
  }

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers, // Add custom headers
  }
  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    if (response.status === 401) {
      // Token might be expired, handle token refresh or redirect to login
      useAuthStore.getState().removeToken()
      // Optionally redirect to login page or refresh token
    }
    // 尝试解析错误响应体
    let errorMessage = response.statusText
    try {
      const errorData = await response.json()
      // NestJS 错误响应通常包含 message 字段
      errorMessage = errorData.message || errorData.error || response.statusText
    } catch (e) {
      // 如果响应不是 JSON 格式，使用 statusText
      errorMessage = response.statusText
    }
    const error = new Error(errorMessage)
    // 保留原始错误数据以便调试
    error.response = { status: response.status, data: errorMessage }
    throw error
  }
  return response
}

const urlWrpper = async (endpoint, params = {}, options = {}) => {
  const method = String(options.method || 'GET').toUpperCase()
  try {
    return await requestOnce(endpoint, params, options)
  } catch (error) {
    // 发版切流量时，进行中的 GET 会被掐断。重试一次打到新实例，页面不必先进入失败态。
    if (method !== 'GET' || !isTransientFetchError(error)) throw error
    await wait(DEPLOY_RETRY_DELAY_MS)
    if (options.signal?.aborted) throw error
    return requestOnce(endpoint, params, options)
  }
}

export const tokenFetcher = async (endpoint, { arg }) => {
  const url = new URL(`${baseUrl}${endpoint}`)

  const headers = {
    'Content-Type': 'application/json',
  }
  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include', // 包括凭证信息
    body: JSON.stringify(arg),
  })
  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().removeToken()
    }
    const errorData = await response.json()
    throw errorData
  }
  const res = await response.json()
  await processAuthResponse(res)
  return res
}

export const registerFetcher = async (endpoint, { arg }) => {
  const url = new URL(`${baseUrl}${endpoint}`)

  const headers = {
    'Content-Type': 'application/json',
  }
  const response = await fetch(url, {
    method: 'POST',
    headers,
    credentials: 'include', // 包括凭证信息
    body: JSON.stringify(arg),
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw errorData
  }

  return response.json()
}

export const delFetcher = async (endpoint, { arg }) => {
  const url = `${endpoint}/${arg.id}`

  const response = await urlWrpper(url, null, {
    method: 'DELETE',
    body: arg.body ? JSON.stringify(arg.body) : null,
  })
  return response.json()
}

export const delUrlFetcher = async (endpoint, { arg }) => {
  const url = `${endpoint}`

  const response = await urlWrpper(url, null, {
    method: 'DELETE',
    body: arg ? JSON.stringify(arg) : null,
  })
  return response.json()
}

export const postFetcher = async (endpoint, { arg }, options = {}) => {
  const response = await urlWrpper(endpoint, null, {
    ...options,
    method: 'POST',
    body: JSON.stringify(arg),
  })
  return response.json()
}

/**
 * POST JSON and consume Server-Sent Events (`data: {...}\n\n`).
 * @param {string} endpoint
 * @param {object} body
 * @param {{
 *   onEvent?: (event: object) => void,
 *   signal?: AbortSignal,
 * }} [options]
 * @returns {Promise<object | undefined>} last `done`/`error` event
 */
export const postSseFetcher = async (endpoint, body, options = {}) => {
  const { onEvent, signal } = options
  const response = await urlWrpper(endpoint, null, {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
    headers: { Accept: 'text/event-stream' },
  })
  if (!response.body) {
    throw new Error('服务器没有返回进度流')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let lastEvent

  const consume = (raw) => {
    const blocks = raw.split('\n\n')
    for (const block of blocks) {
      const dataLines = block
        .split('\n')
        .filter((line) => line.startsWith('data:'))
      if (!dataLines.length) continue
      const payload = dataLines
        .map((line) => line.replace(/^data:\s?/, ''))
        .join('\n')
      if (!payload || payload === '[DONE]') continue
      try {
        const event = JSON.parse(payload)
        lastEvent = event
        onEvent?.(event)
      } catch {
        // ignore malformed chunks
      }
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const parts = buffer.split('\n\n')
    buffer = parts.pop() || ''
    if (parts.length) consume(parts.join('\n\n') + '\n\n')
  }
  if (buffer.trim()) consume(buffer)

  if (lastEvent?.phase === 'error' && !lastEvent.result) {
    throw new Error(lastEvent.message || '写入标签失败')
  }
  return lastEvent
}

/**
 * POST，带上传进度（xhr.upload）。percent 0–100 只覆盖发 body 阶段。
 * onUploaded 在 body 发完、等服务端处理时回调。
 * @param {string} endpoint
 * @param {Document | XMLHttpRequestBodyInit | null} body
 * @param {{
 *   onProgress?: (percent: number) => void,
 *   onUploaded?: () => void,
 *   timeout?: number,
 *   headers?: Record<string, string>,
 * }} [options]
 * @returns {Promise<any>}
 */
const postWithUploadProgress = (
  endpoint,
  body,
  { onProgress, onUploaded, timeout = 120000, headers = {} } = {},
) => {
  const token = useAuthStore.getState().auth?.token
  const url = `${baseUrl}${endpoint}`

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.timeout = timeout
    xhr.responseType = 'json'

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    }
    if (onUploaded) {
      xhr.upload.onload = () => onUploaded()
    }

    xhr.onload = () => {
      let data = xhr.response
      if ((data == null || typeof data === 'string') && xhr.responseText) {
        try {
          data = JSON.parse(xhr.responseText)
        } catch {
          data = xhr.response
        }
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data)
        return
      }
      if (xhr.status === 401) {
        useAuthStore.getState().removeToken()
      }
      const errorMessage =
        (data && (data.message || data.error)) || xhr.statusText
      const error = new Error(
        Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage,
      )
      error.response = { status: xhr.status, data }
      reject(error)
    }
    xhr.onerror = () => reject(new Error('网络错误，上传失败'))
    xhr.ontimeout = () => reject(new Error('上传超时'))
    xhr.send(body)
  })
}

/**
 * POST JSON，带上传进度。
 * @param {string} endpoint
 * @param {object} body
 * @param {{
 *   onProgress?: (percent: number) => void,
 *   onUploaded?: () => void,
 *   timeout?: number,
 * }} [options]
 */
export const postJsonWithProgress = (endpoint, body, options = {}) =>
  postWithUploadProgress(endpoint, JSON.stringify(body), {
    ...options,
    headers: { 'Content-Type': 'application/json' },
  })

/**
 * POST multipart，带上传进度。不要手动设 Content-Type，让浏览器带 boundary。
 * @param {string} endpoint
 * @param {FormData} formData
 * @param {{
 *   onProgress?: (percent: number) => void,
 *   onUploaded?: () => void,
 *   timeout?: number,
 * }} [options]
 */
export const postFormWithProgress = (endpoint, formData, options = {}) =>
  postWithUploadProgress(endpoint, formData, options)

/**
 * @param {string} endpoint
 * @param {{ arg?: FormData, signal?: AbortSignal }} [options]
 */
export const formDataFetcher = async (endpoint, options = {}) => {
  const { arg, signal } = options
  const token = useAuthStore.getState().auth?.token
  const url = new URL(`${baseUrl}${endpoint}`)

  const headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: arg,
    signal,
  })

  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().removeToken()
    }
    let errorMessage = response.statusText
    try {
      const errorData = await response.json()
      errorMessage = errorData.message || errorData.error || response.statusText
    } catch (e) {
      errorMessage = response.statusText
    }
    const error = new Error(errorMessage)
    error.response = { status: response.status, data: errorMessage }
    throw error
  }

  return response.json()
}

export const putFetcher = async (endpoint, { arg }, options = {}) => {
  const response = await urlWrpper(endpoint, null, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(arg),
  })
  return response.json()
}

export const patchFetcher = async (endpoint, { arg }, options = {}) => {
  const response = await urlWrpper(endpoint, null, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(arg),
  })
  return response.json()
}

export const fetcher = async ([endpoint, params = {}, options = {}]) => {
  const response = await urlWrpper(endpoint, params, options)
  return response.json()
}

export const getBlob = async (endpoint) => {
  const token = useAuthStore.getState().auth?.token
  const response = await fetch(`${baseUrl}${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().removeToken()
    }
    let errorMessage = response.statusText
    try {
      const errorData = await response.json()
      errorMessage = errorData.message || errorData.error || response.statusText
    } catch (e) {
      errorMessage = response.statusText
    }
    throw new Error(
      Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage,
    )
  }
  return response.blob()
}

export const getFetcher = async (endpoint, params = {}, options = {}) => {
  const response = await urlWrpper(endpoint, params, {
    ...options,
    method: 'GET',
    credentials: 'include',
  })
  return response.json()
}

export const blobFetcher = async ([endpoint, params = {}, options = {}]) => {
  const response = await urlWrpper(endpoint, params, {
    ...options,
    headers: { 'Content-Type': 'text/csv', Accept: 'text/csv' },
  })
  return response.blob()
}

/**
 * 使用 XMLHttpRequest 下载文件（支持进度回调）
 * @param {string} url - 文件 URL
 * @param {Object} options - 选项
 * @param {Function} options.onProgress - 进度回调函数 (loaded: number, total: number, percent: number)
 * @param {number} options.timeout - 超时时间（毫秒），默认 90000ms (90秒)
 * @returns {Promise<Blob>}
 */
export const downloadFileWithProgress = (url, options = {}) => {
  const { onProgress, timeout = 90000 } = options

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'blob'
    xhr.timeout = timeout

    if (onProgress) {
      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = (event.loaded / event.total) * 100
          onProgress(event.loaded, event.total, percent)
        } else {
          onProgress(event.loaded, 0, 0)
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.response)
      } else {
        reject(new Error(`下载失败: ${xhr.status} ${xhr.statusText}`))
      }
    }

    xhr.onerror = () => {
      reject(new Error('网络错误，下载失败'))
    }

    xhr.ontimeout = () => {
      reject(new Error('下载超时，请检查网络连接'))
    }

    xhr.send()
  })
}

const assembleParams = (params) => {
  const validParams = {}

  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      validParams[key] = value
    }
  }

  // 获取排序后的对象键值对数组
  const sortedEntries = Object.entries(validParams).sort(([keyA], [keyB]) =>
    keyA.localeCompare(keyB),
  )

  // 生成排序后的对象
  return Object.fromEntries(sortedEntries)
}
